import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { CreditActionsResponse, WithdrawDisabledReason } from '@/api';
import { isMeResponseSE } from '@/api/me/utils';
import { queryKeys } from '@/api/utils/queryKeys';
import { ContactVerificationSuccessAction } from '@/components/ModalRenderer/components/SE/contactVerificationSuccess';
import useFeatureFlag from '@/featureFlags/useFeatureFlag';
import usePendingTransactionPolling from '@/features/products/hooks/usePendingTransactionPolling';
import useModal from '@/hooks/useModal/useModal';
import type {
  WithdrawAccessReturn,
  WithdrawAccessStatus,
  WithdrawNavigationOptions,
} from '@/pages/Credit/hooks/useWithdrawAccessGeneric/types';
import useWithdrawAccessGeneric from '@/pages/Credit/hooks/useWithdrawAccessGeneric/useWithdrawAccessGeneric';
import { bankAccountQueryOptions } from '@/queries/bankAccountQuery';
import { selectWithdrawAction } from '@/queries/creditActionsQuery/creditActionsQuerySelectors';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { offersQueryOptions } from '@/queries/offersQuery/offersQuery';
import { selectHasActivePaymentFreeMonthOffer } from '@/queries/offersQuery/offersQuerySelectors';
import { overdueInfoQueryOptions } from '@/queries/overdueInfoQuery/overdueInfoQuery';
import { selectIsOverdue } from '@/queries/overdueInfoQuery/overdueInfoQuerySelectors';
import { routeMap } from '@/routes/routeMap';
import { assertCondition } from '@/utils/typescript';

const HANDLED_BLOCKED_REASONS: WithdrawDisabledReason[] = [
  'MobileVerificationNeeded',
  'EmailVerificationNeeded',
  'IpAddressCheck',
  'Blocked',
  'UserGotPaymentFreeMonthRecently',
  'MigrationToNewTierNeeded',
];

const useWithdrawStatus = (): WithdrawAccessStatus => {
  const { status, blockedReasons } = useWithdrawAccessGeneric();

  const { data: bankAccount, isPending: isBankAccountPending } = useQuery({
    ...bankAccountQueryOptions(),
    select: bankAccount => {
      const isBankAccountConnected = !!bankAccount.accountNumber;
      const isDirectDebitMandateNotSet = bankAccount.directDebitMandateStatus === 'NOT_SET';

      return {
        isBankAccountConnected,
        isDirectDebitMandateNotSet,
      };
    },
  });
  const { data: hasActivePaymentFreeMonthOffer, isPending: isOffersPending } = useQuery({
    ...offersQueryOptions(),
    select: selectHasActivePaymentFreeMonthOffer,
  });
  const { data: isPaymentProviderMigrationNeeded, isPending: isPaymentProviderMigrationNeededPending } =
    useQuery({
      ...meQueryOptions(),
      select: me => {
        assertCondition(isMeResponseSE(me));
        return me.paymentProvider === 'Trustly';
      },
    });
  const { data: isOverdue, isPending: isOverduePending } = useQuery({
    ...overdueInfoQueryOptions(),
    select: selectIsOverdue,
  });
  const { data: pendingTransaction, isPending: isPendingTransactionPending } = usePendingTransactionPolling();

  const isTransactionPendingOrDelayed =
    !!pendingTransaction?.pendingTransaction || !!pendingTransaction?.delayed;

  const hasUnhandledBlockedReason = blockedReasons.some(reason => !HANDLED_BLOCKED_REASONS.includes(reason));

  const isLoading =
    isBankAccountPending ||
    isOffersPending ||
    isPaymentProviderMigrationNeededPending ||
    isOverduePending ||
    isPendingTransactionPending;
  const isDisabled =
    !bankAccount?.isBankAccountConnected ||
    bankAccount?.isDirectDebitMandateNotSet ||
    !!isPaymentProviderMigrationNeeded ||
    !!hasActivePaymentFreeMonthOffer ||
    !!isOverdue ||
    isTransactionPendingOrDelayed ||
    hasUnhandledBlockedReason;

  if (status === 'loading' || isLoading) {
    return 'loading';
  }

  if (status === 'disabled' || isDisabled) {
    return 'disabled';
  }

  return status;
};

const useWithdrawAccess = (): WithdrawAccessReturn => {
  const { openModal } = useModal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const generic = useWithdrawAccessGeneric();
  const status = useWithdrawStatus();
  const isEmailVerificationEnabled = useFeatureFlag('emailVerificationNeededForWithdraw');

  // Read from the query cache so post-verification resume sees fresh reasons without waiting for a re-render.
  const getBlockedReasons = useCallback((): WithdrawDisabledReason[] => {
    const creditActions = queryClient.getQueryData<CreditActionsResponse>(queryKeys.creditActions);
    if (!creditActions) {
      return generic.blockedReasons;
    }

    const withdraw = selectWithdrawAction(creditActions);
    if (!withdraw.disabled) {
      return [];
    }

    if (isEmailVerificationEnabled || !withdraw.reasons.includes('EmailVerificationNeeded')) {
      return withdraw.reasons;
    }

    return withdraw.reasons.filter(reason => reason !== 'EmailVerificationNeeded');
  }, [generic.blockedReasons, isEmailVerificationEnabled, queryClient]);

  const attemptWithdrawNavigation = useCallback(
    (options?: WithdrawNavigationOptions) => {
      const blockedReasons = getBlockedReasons();

      if (blockedReasons.includes('MobileVerificationNeeded')) {
        openModal('SE.MOBILE_VERIFICATION', {
          isPersistedFromRedirect: true,
          payload: { successAction: ContactVerificationSuccessAction.ResumeWithdraw },
        });
        void navigate(routeMap.home, { replace: true });
      } else if (blockedReasons.includes('EmailVerificationNeeded')) {
        openModal('SE.EMAIL_VERIFICATION', {
          isPersistedFromRedirect: true,
          payload: { successAction: ContactVerificationSuccessAction.ResumeWithdraw },
        });
        void navigate(routeMap.home, { replace: true });
      } else if (blockedReasons.includes('IpAddressCheck')) {
        openModal('SE.WITHDRAWAL_IP_ADDRESS_CHECK');
      } else if (blockedReasons.includes('Blocked')) {
        openModal('WITHDRAWAL_BLOCKED');
      } else if (blockedReasons.includes('UserGotPaymentFreeMonthRecently')) {
        openModal('SE.PAYMENT_FREE_MONTH_WITHDRAWAL_BLOCKED');
      } else if (blockedReasons.includes('MigrationToNewTierNeeded')) {
        openModal('SE.MIGRATION_TO_NEW_TIER_NEEDED');
      } else {
        generic.attemptWithdrawNavigation(options);
      }
    },
    [generic, getBlockedReasons, navigate, openModal],
  );

  return { status, attemptWithdrawNavigation };
};

export default useWithdrawAccess;
